import React, { useRef, useState } from "react";
import * as Yup from "yup";
import dynamic from "next/dynamic";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { ReCAPTCHA } from "@/lib/vendor";
import { Formik, Form, Field, ErrorMessage } from "formik";

import { downloadFile } from "@/utils/common";
import apiService from "@/common/services/apiService";
const Button = dynamic(() => import("@/Components/Button"));
import { hideFormLoader, showFormLoader } from "@/store/loaderSlice";

interface QuoteFormValues {
  email: string;
  full_name: string;
}

const initialValues: QuoteFormValues = {
  full_name: "",
  email: "",
};

interface PostFormProps {
  data?: any;
}

const PostForm: React.FC<PostFormProps> = ({ data }) => {
  const dispatch = useDispatch();
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const [isLoading, setIsLoading] = useState(false);
  const sitekey = process.env.NEXT_PUBLIC_RECAPTCHA_V3;
  const validationSchema = Yup.object({
    full_name: Yup.string().trim().required("Full name is required."),
    email: Yup.string()
      .trim()
      .email("Email is not valid.")
      .max(100, "Email is too long")
      .required("Email is required.")
      .matches(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/, "Invalid email format"),
  });
  const validateCaptchaV3 = () => {
    return new Promise((resolve) => {
      reCaptchaRef?.current?.executeAsync()?.then(async (value: unknown) => {
        resolve(value);
        // const captchaRes = await axios.post(`/api/validateCaptchaV3`, {token: value});
        // if (captchaRes.status === 200 && captchaRes.data.success) resolve(true); else reject(false);
      });
    });
  };

  const handleSubmit = async (
    values: QuoteFormValues,
    { resetForm, setSubmitting }: any
  ) => {
    try {
      setIsLoading(true);
      const isHuman = await validateCaptchaV3();
      if (!isHuman) return false;
      dispatch(showFormLoader());
      const payload = {
        fullname: values.full_name,
        email: values.email,
        page_link: window.location.href,
        company_name: "",
        job_title: "",
        captcha_token_v3: isHuman as string,
      } as Record<string, any>;
      await apiService
        .authPost("download-leads/create", payload)
        .then((res: any) => {
          dispatch(hideFormLoader());
          resetForm();
          setSubmitting(false);
          if (!res.data.success)
            toast.error(res?.data?.message ?? "Something went wrong");
          else if (res.status === 200) {
            reCaptchaRef?.current?.reset();
            toast.success(res?.data?.message ?? "Your request has been sent.");
            const attributes = data?.download_blog?.data?.attributes;
            if (attributes?.url) downloadFile(attributes.url, attributes.name);
          } else toast.error(res?.data?.message ?? "Something went wrong");
        });
    } catch (error) {
      console.log("handleSubmit ~ error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="px-[0px] md:px-[40px] xl:px-[72px] relative z-[0] overflow-hidden py-[56px] lg:py-[80px] xl:py-[80px] bg-white before:content-[''] before:absolute before:h-[100%] before:inset-0 before:bg-[url('/images/client-bg.png')] before:bg-no-repeat before:bg-[length:100%_100%] before:bg-center before:bg-[#F0F8FF] before:-z-10 my-[40px]">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-[24px] xl:gap-[30px] items-center">
        <div className="heading">
          <h2 className="mb-[16px] lg:text-[48px] xl:text-[40px] xm:text-[32px] xm:text-[32px] text-[26px]  xl:leading-[52px] lg:leading-[62.4px] xm:leading-[41.6px] leading-[35px] font-[700] text-[#040405]">
            {data?.title ?? "Download this EBook for offline"}
          </h2>
          <p className="text-[#32343B] font-[400] text-[20px] leading-[26px]">
            {data?.text ?? ""}
          </p>
        </div>
        <div>
          <div>
            <Formik
              onSubmit={handleSubmit}
              initialValues={initialValues}
              validationSchema={validationSchema}
            >
              {({ values, handleChange }) => (
                <Form aria-label="Request for Proposal Form">
                  <div>
                    <div className="grid lg:grid-cols-1 gap-4 h-full items-stretch">
                      <div className="input-group relative w-full">
                        <Field
                          type="text"
                          name="full_name"
                          value={values.full_name}
                          placeholder=""
                          aria-label={"Full name"}
                          aria-describedby={`full_name-error`}
                          aria-required={true}
                          onChange={handleChange}
                          className="input text-subtitle-3 font-bold mb-[10px]"
                        />
                        <label
                          htmlFor="full_name"
                          className="text-subtitle-5 font-regular"
                        >
                          Full name*
                        </label>
                        <ErrorMessage
                          name="full_name"
                          component="span"
                          className="text-red-500"
                        />
                      </div>
                      <div className="input-group relative w-full">
                        <Field
                          type="email"
                          name="email"
                          placeholder=""
                          value={values.email}
                          aria-label={"email"}
                          aria-required={true}
                          onChange={handleChange}
                          aria-describedby={`email-error`}
                          className="input text-subtitle-3 font-bold mb-[10px]"
                        />
                        <label
                          htmlFor="email"
                          className="text-subtitle-5 font-regular"
                        >
                          email*
                        </label>
                        <ErrorMessage
                          name="email"
                          component="span"
                          className="text-red-500"
                        />
                      </div>
                      <div>
                        <Button
                          type="submit"
                          variant="small"
                          className="my-0"
                          loading={isLoading}
                          label={data?.button_text || "Submit"}
                        />
                      </div>
                    </div>
                  </div>

                  {sitekey && (
                    <ReCAPTCHA
                      style={{ display: "inline" }}
                      theme="dark"
                      size="invisible"
                      ref={reCaptchaRef}
                      sitekey={sitekey}
                    />
                  )}
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};
export default PostForm;
